<script setup>
import { Head, usePage } from '@inertiajs/vue3';
import { getCurrentInstance, computed } from 'vue';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import MainLayout from '@/layout/main/MainLayout.vue';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const page = usePage();

const isAdmin = computed(() => {
    const currentUrl = window.location.href;
    if (currentUrl.includes('/admin')) return true;
    
    const user = page.props.auth?.user;
    if (user && user.role) {
        return user.role === 'admin' || user.role === 'administrator';
    }
    
    return false;
});

const currentLayout = computed(() => {
    return isAdmin.value ? AdminMainLayout : MainLayout;
});

const goHome = () => {
    if (isAdmin.value) {
        window.location.href = '/admin/dashboard';
    } else {
        window.location.href = '/dashboard';
    }
};

const goBack = () => {
    window.history.back();
};

defineOptions({
    layout: (h, page) => {
        const isAdminPage = window.location.href.includes('/admin') || 
                           page.props.auth?.user?.role === 'admin' || 
                           page.props.auth?.user?.role === 'administrator';
        
        return h(isAdminPage ? AdminMainLayout : MainLayout, () => page);
    },
});
</script>

<template>
    <div class="flex items-center justify-center min-h-[85vh] px-4">
        <Head :title="$t('404 - Page not found')" />
        
        <div class="text-center max-w-2xl mx-auto">
            <!-- Pet Image -->
            <div class="mb-8 flex justify-center">
                <div class="relative">
                    <img 
                        src="/images/linking-pet.png" 
                        :alt="$t('404 Robot')" 
                        class="w-64 h-auto mx-auto drop-shadow-lg"
                    />
                </div>
            </div>

            <!-- Error Message -->
            <div class="space-y-6">
                <div>
                    <h1 class="text-6xl font-bold text-gray-800 mb-4">
                        4<span class="text-blue-500 animate-pulse">0</span>4
                    </h1>
                    <h2 class="text-2xl font-semibold text-gray-700 mb-3">
                        {{ $t('Oops! Page not found') }}
                    </h2>
                    <p class="text-lg text-gray-600 max-w-lg mx-auto leading-relaxed">
                        {{ $t('Our robot searched all corners of the internet, but could not find the page you are looking for.') }}
                    </p>
                </div>

                <!-- Action Buttons -->
                <div class="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
                    <button
                        @click="goHome"
                        class="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-200 flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                        <i class="pi pi-home"></i>
                        {{ $t('Back to home') }}
                    </button>
                    <button
                        @click="goBack"
                        class="px-6 py-3 border-2 border-gray-300 hover:border-gray-400 text-gray-700 hover:text-gray-800 font-semibold rounded-lg transition-colors duration-200 flex items-center gap-2 bg-white hover:bg-gray-50 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                        <i class="pi pi-arrow-left"></i>
                        {{ $t('Previous page') }}
                    </button>
                </div>

                <!-- Additional Help -->
                <div class="pt-8 border-t border-gray-200 mt-12 max-w-lg mx-auto">
                    <p class="text-sm text-gray-500 mb-4">
                        {{ $t('If you believe this is an error, please contact us:') }}
                    </p>
                    <div class="flex justify-center gap-6 text-sm">
                        <a class="text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors">
                            <i class="pi pi-envelope"></i>
                            contato@linkarme.com
                        </a>
                        <a class="text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors">
                            <i class="pi pi-phone"></i>
                            +55 47 99172-8180
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
@keyframes glitch {
    0% { transform: translate(0); }
    20% { transform: translate(-2px, 2px); }
    40% { transform: translate(-2px, -2px); }
    60% { transform: translate(2px, 2px); }
    80% { transform: translate(2px, -2px); }
    100% { transform: translate(0); }
}

.animate-glitch {
    animation: glitch 0.3s infinite;
}
</style>